import { archiproImageLoader, DEVICE_SIZES } from '@rocco/components/image';

/**
 * The viewer is where a reader looks closely, so it keeps the quality the
 * legacy lightbox asked the CDN for rather than the site's default of 80.
 */
const VIEWER_QUALITY = 90;

const cdnLoader = archiproImageLoader();

const WIDEST = Math.max(...DEVICE_SIZES);

/**
 * The width to fetch viewer slides at: the screen's device pixels, rounded up
 * to a CDN size so a slide fetched ahead of time and the slide shown are one
 * URL, and one cache entry.
 *
 * The screen's width is the most a photograph can fill, so it is enough for
 * any shape: a portrait shot is bound by the height and shown narrower. The
 * server has no screen and draws no viewer, so it gets the widest size.
 */
export const getViewerWidth = (): number => {
    if (typeof window === 'undefined') return WIDEST;
    const needed = window.innerWidth * window.devicePixelRatio;
    return DEVICE_SIZES.find((size) => size >= needed) ?? WIDEST;
};

/**
 * A slide's picture, rendered by the CDN at `width`.
 *
 * Slides carry the image service's own edit URL, which is the full crop at
 * whatever box it was baked into — around 670 KB for a project photograph —
 * whatever screen it is shown on. Anything that is not a site-relative image
 * path (an external URL, or one that is already a CDN render) is returned as
 * it came.
 */
export const getViewerImageSrc = (src: string, width: number): string => {
    if (!src.startsWith('/images/') || src.startsWith('/images/cdn-images/')) {
        return src;
    }
    return cdnLoader({ src, width, quality: VIEWER_QUALITY });
};
